
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { lazy } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import ModalOverlay from '@/components/ModalOverlay/ModalOverlay';
import { useIsMobileViewport } from '@/hooks/useIsMobileViewport';
import ShareModal from '@/components/ShareModal/ShareModal';
import SaveToOverlay from '@/components/SaveToOverlay/SaveToOverlay';
import SignUpCTAPopover from '@/components/SignUpCTAPopover/SignUpCTAPopover';
import LoginModal from '@/components/LoginModal/LoginModal';
import BookingModal from '@/components/BookingModal/BookingModal';
import BookingMethodSelectionModal from '@/components/BookingMethodSelectionModal/BookingMethodSelectionModal';
import BookingSsoBlockedModal from '@/components/BookingModal/BookingSsoBlockedModal';
import BookingErrorModal from '@/components/BookingModal/BookingErrorModal';
import { MISSING_PLACE_ERROR, type BookingErrorCopy } from '@/lib/booking/bookingErrors';
import PlaceModalToolbar from './PlaceModalToolbar';
import PlaceModalBody from './PlaceModalBody';
import PlaceModalPhotosTab from './PlaceModalPhotosTab';
import type { VisitStatus } from '@/components/TimelinePlaceCard/BeenHereRating';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { fetchPostsByLocationIdUnified, getUserItineraries, addPlaceToItinerary, getUserLists, addPlaceToList, createItineraryWithPlace, type SavedItinerary } from '@/api/itinerary';
import { recordBookingClick } from '@/api/creators';
import { searchLocationByName, getLocationById, putLocationVisit } from '@/api/location';
import { useAuth } from '@/contexts';
import { useListOptional } from '@/contexts/ListContext';
import { useOptionalLocationBookmarkState } from '@/contexts/LocationBookmarkContext';
import { useLocationBookmarkState } from '@/hooks/useLocationBookmarkState';
import { useLocationSaveToggle } from '@/hooks/useLocationSaveToggle';
import { usePostActions } from '@/hooks/usePostActions';
import { usePostShare } from '@/hooks/usePostShare';
import { useAuthAction } from '@/hooks/useAuthAction';
import { useRecommendationCache } from '@/hooks/useRecommendationCache';
import { useNuiteeHotelEnrichment } from '@/hooks/useNuiteeHotelEnrichment';
import {
  extractLocationDataForDisplay,
  getRecommenderAvatarsWithIdsFromLocationLike,
  type RecommenderAvatar,
} from '@/utils/locationDataExtractor';
import { extractPhotosFromLocation } from '@/utils/photoUtils';
import { extractAllPostPhotos } from '@/utils/postPhotoExtraction';
import { getPostDestinationLabel } from '@/utils/postPresentationNormalizer';
import { getTripCardDestinationCount } from '@/utils/tripCardDestinationCount';
import { fetchWebsiteIfMissing } from '@/utils/websiteUtils';
import { getGoogleEditorialSummary } from '@/utils/googlePlaceOverview';
import { debug, error as logError } from '@/utils/logger';
import { isAuthRequiredError } from '@/utils/errorHelpers';
import { ENABLE_IN_APP_API_BOOKING, isGygExperiencesEnabled, isStay22Enabled } from '@/config/bookingExperience';
import { isUserAdmin } from '@/utils/cognitoGroups';
import { useOpenWhitelabelForPlace } from '@/hooks/useOpenWhitelabelForPlace';
import { useOpenGygDeepLink } from '@/hooks/useOpenGygDeepLink';
import { useOpenStay22DeepLink } from '@/hooks/useOpenStay22DeepLink';
import type { Stay22AllezProvider } from '@/lib/booking/stay22AllezProviders';
import { resolveStay22LocationFromPlace } from '@/lib/booking/stay22LocationParams';
import { canShowGygForLocation } from '@/utils/booking/experiencePlaceEligibility';
import { isStayPlace } from '@/utils/placeCategoryDisplay';
import { buildGygSearchQueryFromLocation, type GygLocationInput } from '@/utils/booking/buildGygSearchQuery';
import { WebAnalytics } from '@/lib/analytics';
import { getReferralCreatorIdForApi } from '@/lib/analyticsAttribution';
import {
  EVENT_PLACE_MODAL_OPENED,
  EVENT_PLACE_MODAL_TAB_SELECTED,
  type PlaceModalEntryPoint,
  EVENT_PLACE_SAVE_TO_ITINERARY_CLICKED,
  EVENT_PLACE_SAVED_TO_ITINERARY,
  EVENT_PLACE_CREATE_TRIP_CLICKED,
  EVENT_PLACE_CREATE_TRIP_SUCCEEDED,
  EVENT_LOGGED_OUT_PLACE_SAVE_CLICKED,
  EVENT_LOCATION_SHARE_CLICKED,
  EVENT_PLACE_STAY_BOOK_CHOICE_SELECTED,
} from '@/lib/analyticsEvents';
import Post from '@/models/post';
import type { PlaceCardData } from '@/types/listViewTypes';
import type { RawLocationData } from '@/utils/postTypes';
import type { Location as LocationModel } from '@/models/locationModel';
import Location from '@/models/location';
import type { BookingContext } from '@/components/BookingModal/BookingModal';
import type { PlaceModalSearchParams } from './PlaceModalBookingWidget';
import { getCanonicalLocationMongoId } from '@/utils/canonicalLocationId';
import { resolveLocationIdForSave } from '@/utils/resolveLocationIdForSave';
import { isMongoObjectIdString } from '@/utils/idGeneration';
import {
  getMyRecommendationForLocation,
  type EnrichedRecommendationDoc,
} from '@/api/enrichedRecommendation';
import clsx from 'clsx';

/** Dynamic import avoids a static circular dependency (RecommendThisPlaceModal imports this module). */
const RecommendThisPlaceModalLazy = lazy(() => import('@/components/RecommendThisPlaceModal/RecommendThisPlaceModal'));

/**
 * Shell matches Figma "Place Page": desktop side panel docked right (806px — Figma's 672px
 * widened by 20%, full height, 24px left radius); mobile takes the full screen (Figma "· Mobile" frames).
 */
const MODAL_PANEL_ROOT = clsx(
  'flex flex-col bg-white overflow-y-auto overflow-x-hidden',
  'md:h-[100dvh] md:w-[806px] md:max-w-[100vw] md:rounded-l-[24px]',
  'md:shadow-[0px_-2px_6px_rgba(0,0,0,0.04),0px_-8px_32px_rgba(0,0,0,0.2)]',
  'md:animate-place-panel-enter max-md:animate-combined-place-modal-enter',
  // Scrollable but no visible scrollbar — keeps rounded corners flush
  '[scrollbar-width:none] [-ms-overflow-style:none] [-webkit-overflow-scrolling:touch]',
  '[&::-webkit-scrollbar]:hidden',
  'max-md:h-[100dvh] max-md:w-full',
);

interface CombinedContentPlaceModalProps {
  isOpen: boolean;
  onClose: () => void;
  locationId?: string;
  locationData?: Partial<LocationModel>;
  /** Optional post id for booking attribution (e.g. when opened from a map pin on a post view). */
  attributionPostId?: string;
  /** Step/sequence number from the parent list. Not shown in the Place Page design; kept for callers. */
  displayNumber?: string;
  /** Itinerary pin fill from day index. Not shown in the Place Page design; kept for callers. */
  sequencePinFill?: string;
  /** Where the modal was triggered from — forwarded to EVENT_PLACE_MODAL_OPENED for funnel analysis. */
  entryPoint?: PlaceModalEntryPoint;
  /**
   * Extra CSS class forwarded to the ModalOverlay root div.
   * Use 'modal-overlay--above-recommend' when this modal is opened from inside
   * RecommendThisPlaceModal so it stacks above it.
   */
  overlayClassName?: string;
}

const CombinedContentPlaceModal: React.FC<CombinedContentPlaceModalProps> = ({
  isOpen,
  onClose,
  locationId,
  locationData,
  attributionPostId,
  entryPoint,
  overlayClassName,
}) => {
  const router = useNavigate();
  const pathname = useLocation().pathname;
  const isMobileViewport = useIsMobileViewport();
  const { user } = useAuth();
  const isLoggedIn = !!user;
  const { handleSave, handleUnsave } = usePostActions();
  const {
    requireAuth,
    showPopover,
    getPopoverProps,
    showLoginModal,
    setShowLoginModal,
    loginModalRedirectPath,
    handleSignUpClick,
  } = useAuthAction();
  const {
    isRecommended,
    addRecommendation,
    bumpExplicitRecDelta,
    explicitRecDeltaFor,
  } = useRecommendationCache();

  const bookmarkFromPage = useOptionalLocationBookmarkState();
  const listCtx = useListOptional();
  const localBookmarkState = useLocationBookmarkState({
    user,
    shouldHydrate: !!user && !bookmarkFromPage,
  });
  const bookmarkState = bookmarkFromPage ?? localBookmarkState;

  const saveSourcePostId =
    attributionPostId ??
    (listCtx?.listPost?._id != null ? String(listCtx.listPost._id) : undefined) ??
    listCtx?.editingPostId ??
    undefined;

  const { handleLocationSaveToggle: syncLocationBookmarkToggle } = useLocationSaveToggle({
    user,
    requireAuth,
    bookmarkState,
    sourcePostId: saveSourcePostId,
  });

  // ── View state ──────────────────────────────────────────────────────────────
  /** The Place Page has no tabs; the gallery opens a full photo grid in place of the details. */
  const [view, setView] = useState<'details' | 'photos'>('details');
  /** "I've been here" rating: seeded from v2 `my_visit`, saved via PUT /api/v2/locations/:id/visit. */
  const [visitStatus, setVisitStatus] = useState<VisitStatus>({ rating: null, note: '' });
  /** Bumped after recommend / unrecommend or enriched save so Community Tips refetch without full page reload. */
  const [gtReviewsRefreshSignal, setGtReviewsRefreshSignal] = useState(0);
  const refreshGtReviewsData = useCallback(() => {
    setGtReviewsRefreshSignal((n) => n + 1);
  }, []);

  /** Keeps the existing tab analytics: photos grid → 'photos', Read More tips → 'gtReviews'. */
  const trackSectionSelected = useCallback((tab: 'overview' | 'photos' | 'gtReviews') => {
    WebAnalytics.track(EVENT_PLACE_MODAL_TAB_SELECTED, {
      tab,
      locationId: locationId ?? null,
      locationName: locationData?.name ?? null,
    });
  }, [locationId, locationData?.name]);

  const handleViewChange = useCallback((next: 'details' | 'photos') => {
    setView(next);
    trackSectionSelected(next === 'photos' ? 'photos' : 'overview');
  }, [trackSectionSelected]);

  // Reset to the details view when a new place opens
  const prevLocationIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (isOpen && locationId !== prevLocationIdRef.current) {
      prevLocationIdRef.current = locationId;
      setView('details');
      setVisitStatus({ rating: null, note: '' });
    }
    if (!isOpen) prevLocationIdRef.current = undefined;
  }, [isOpen, locationId]);

  // ── Associated posts ────────────────────────────────────────────────────────
  const [associatedPosts, setAssociatedPosts] = useState<Post[]>([]);
  const [postsLoading, setPostsLoading] = useState<boolean>(false);
  const [postsError, setPostsError] = useState<string | null>(null);
  const [postsNeedSignIn, setPostsNeedSignIn] = useState(false);
  const [totalPostsCount, setTotalPostsCount] = useState<number>(0);
  const [savedMap, setSavedMap] = useState<Record<string, boolean>>({});

  // ── Share modal state ───────────────────────────────────────────────────────
  const { handleShareClick: sharePost, shareModal } = usePostShare();
  const [isLocationShareModalOpen, setIsLocationShareModalOpen] = useState(false);

  // ── Location derived state ──────────────────────────────────────────────────
  const [fetchedWebsite, setFetchedWebsite] = useState<string | undefined>(undefined);
  const [fetchedExplicitRecCount, setFetchedExplicitRecCount] = useState<number | null>(null);
  const [fetchedPostLocationCount, setFetchedPostLocationCount] = useState<number | null>(null);
  /** True when the fetched counts came from v2 `/location/:id` (by _id), not the name search. */
  const [recCountsFromDetails, setRecCountsFromDetails] = useState(false);
  const [fetchedRecAvatars, setFetchedRecAvatars] = useState<RecommenderAvatar[] | null>(null);
  const [enrichedRecommendLocation, setEnrichedRecommendLocation] = useState<Location | null>(null);
  /**
   * Pre-fetched recommendation doc for the current location.
   * undefined = not yet fetched; null = fetched and none found; populated = edit mode.
   */
  const [enrichedRecommendInitialDoc, setEnrichedRecommendInitialDoc] = useState<EnrichedRecommendationDoc | null | undefined>(undefined);

  // ── SaveToOverlay state ─────────────────────────────────────────────────────
  const [isSaveToOverlayOpen, setIsSaveToOverlayOpen] = useState(false);
  const [selectedPlaceForSave, setSelectedPlaceForSave] = useState<PlaceCardData | null>(null);
  const [userItineraries, setUserItineraries] = useState<SavedItinerary[]>([]);
  const [isLoadingItineraries, setIsLoadingItineraries] = useState(false);
  const [userLists, setUserLists] = useState<SavedItinerary[]>([]);
  const [isLoadingLists, setIsLoadingLists] = useState(false);
  const [isCreatingTrip, setIsCreatingTrip] = useState(false);

  // ── Nuitee hotel enrichment ─────────────────────────────────────────────────
  const nuiteeHotelId = locationData?.nuitee_hotel_id;
  const { enrichedIds, isEnriching } = useNuiteeHotelEnrichment(isOpen ? nuiteeHotelId : null);
  // Whitelabel Book for these hotels passes nuiteeHotelId straight through: looking a hotel up
  // from a place id returns the first hotel within 1 km, and their google_place_id is city-level.
  // This is the hotel's own place id, for the booking reference.
  const nuiteeHotelGooglePlaceId = nuiteeHotelId ? enrichedIds?.hotel_google_place_id ?? null : null;

  // ── Nuitee hotel → Mongo location resolution ──────────────────────────────
  const [resolvedMongoId, setResolvedMongoId] = useState<string | null>(null);
  const [isResolvingMongoId, setIsResolvingMongoId] = useState(false);

  useEffect(() => {
    if (!isOpen || !nuiteeHotelId || !enrichedIds?.hotel_google_place_id) {
      return;
    }
    const existingId = locationData?._id ?? (locationData as Record<string, unknown> | undefined)?.location_id as string | undefined;
    if (existingId && isMongoObjectIdString(existingId)) return;

    let cancelled = false;
    setIsResolvingMongoId(true);

    resolveLocationIdForSave({
      googlePlaceId: enrichedIds.hotel_google_place_id,
      name: locationData?.name ?? undefined,
      address: locationData?.a3_intl_address ?? locationData?.address ?? undefined,
    })
      .then((result) => {
        if (cancelled) return;
        if (result) {
          debug('[CombinedModal] Nuitee hotel resolved to Mongo location', {
            nuiteeHotelId,
            hotelGooglePlaceId: enrichedIds.hotel_google_place_id,
            resolvedLocationId: result.locationId,
            resolvedViaGoogle: result.resolvedViaGoogle,
          });
          setResolvedMongoId(result.locationId);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        logError('[CombinedModal] Failed to resolve Nuitee hotel to Mongo location', err);
      })
      .finally(() => {
        if (!cancelled) setIsResolvingMongoId(false);
      });

    return () => { cancelled = true; };
  }, [isOpen, nuiteeHotelId, enrichedIds?.hotel_google_place_id, locationData]);

  /** Canonical Mongo `locations._id` for recommend/save/cache; Nuitee rows use `resolvedMongoId` after enrichment. */
  const effectiveLocationMongoId = useMemo(() => {
    if (locationData) {
      const fromPayload =
        (locationData._id as string | undefined) ||
        (locationData as { location_id?: string }).location_id;
      if (fromPayload && isMongoObjectIdString(fromPayload)) return fromPayload;
    }
    if (locationId && isMongoObjectIdString(locationId)) return locationId;
    return resolvedMongoId;
  }, [locationData, locationId, resolvedMongoId]);

  // ── Booking modal state ─────────────────────────────────────────────────────
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [bookingContext, setBookingContext] = useState<BookingContext | null>(null);
  const [placeModalSearchParams, setPlaceModalSearchParams] = useState<PlaceModalSearchParams | null>(null);
  const [isBookingMethodSelectionOpen, setIsBookingMethodSelectionOpen] = useState(false);
  const {
    openWhitelabelForPlace,
    isOpening: isBookOpeningWhitelabel,
    blockedLoginUrl: bookingBlockedLoginUrl,
    clearBlockedState: clearBookingBlockedState,
    bookingError,
    clearBookingError,
  } = useOpenWhitelabelForPlace();
  const {
    openGygDeepLink,
    isOpening: isExperiencesOpening,
    blockedUrl: experiencesBlockedUrl,
    clearBlockedState: clearExperiencesBlockedState,
  } = useOpenGygDeepLink();
  const {
    openStay22DeepLink,
    isOpening: isStay22AllezOpening,
    blockedUrl: stay22AllezBlockedUrl,
    clearBlockedState: clearStay22AllezBlockedState,
    bookingError: stay22Error,
    clearBookingError: clearStay22Error,
  } = useOpenStay22DeepLink();

  /** Book was tapped on a place that can't be booked at all (no place id or coordinates). */
  const [stayPlaceError, setStayPlaceError] = useState<BookingErrorCopy | null>(null);
  const shownBookingError = stayPlaceError ?? bookingError ?? stay22Error;
  const closeBookingError = useCallback(() => {
    setStayPlaceError(null);
    clearBookingError();
    clearStay22Error();
  }, [clearBookingError, clearStay22Error]);

  const validAttributionPostId =
    attributionPostId && /^[0-9a-fA-F]{24}$/.test(attributionPostId) ? attributionPostId : undefined;

  // ── Fetch associated posts ──────────────────────────────────────────────────
  const fetchAssociatedPosts = useCallback(async () => {
    if (!isOpen || !locationId) return;
    if (!/^[a-f\d]{24}$/i.test(locationId)) {
      setPostsLoading(false);
      return;
    }
    setPostsLoading(true);
    setPostsError(null);
    setPostsNeedSignIn(false);
    try {
      debug('[CombinedModal] Fetching associated posts', { locationId });
      const result = await fetchPostsByLocationIdUnified(locationId, { limit: 24 });
      setAssociatedPosts(result.posts);
      setTotalPostsCount(result.pagination.totalPosts);
      const map: Record<string, boolean> = {};
      result.posts.forEach((p: Post) => { map[p._id] = p.is_saved || false; });
      setSavedMap(map);
    } catch (err) {
      logError('[CombinedModal] Error fetching associated posts', err);
      // Logged-out reads are public on the backend; fall back to the sign-in prompt where they aren't yet
      if (!isLoggedIn && isAuthRequiredError(err)) {
        setPostsNeedSignIn(true);
      } else {
        setPostsError(err instanceof Error ? err.message : 'Failed to load trips & lists');
      }
    } finally {
      setPostsLoading(false);
    }
  }, [isOpen, locationId, isLoggedIn]);

  useEffect(() => {
    if (isOpen) {
      void fetchAssociatedPosts();
      const loc = locationData as Record<string, unknown> | undefined;
      const isUserCreatedPlace = !!(loc?.is_user_created === true || loc?.isUserCreated === true);
      WebAnalytics.track(EVENT_PLACE_MODAL_OPENED, {
        locationId: locationId ?? null,
        locationName: locationData?.name ?? null,
        isNuiteeHotel: !!locationData?.nuitee_hotel_id,
        is_user_created: isUserCreatedPlace,
        entry_point: entryPoint ?? null,
      });
    } else {
      setAssociatedPosts([]);
      setPostsError(null);
      setPostsNeedSignIn(false);
      setTotalPostsCount(0);
      setSavedMap({});
      setFetchedWebsite(undefined);
      setFetchedExplicitRecCount(null);
      setFetchedPostLocationCount(null);
      setRecCountsFromDetails(false);
      setFetchedRecAvatars(null);
      setEnrichedRecommendLocation(null);
      setEnrichedRecommendInitialDoc(undefined);
    }
  }, [isOpen, fetchAssociatedPosts, locationId, locationData, entryPoint]);

  // ── Derived location data ───────────────────────────────────────────────────
  const extractedLocationData = useMemo(() => {
    if (!locationData) {
      const locId = locationId ?? undefined;
      const globalDelta = explicitRecDeltaFor(effectiveLocationMongoId ?? locId);
      return {
        name: 'Unknown location',
        address: 'Address not available',
        image: '',
        phone: undefined,
        website: undefined,
        recommendations: 0,
        recommendationAvatars: [],
        explicitRecommendations: Math.max(0, 0 + globalDelta),
        traveller_types: [],
        locationId,
      };
    }
    const extracted = extractLocationDataForDisplay(locationData);
    const locId = extracted.locationId ?? locationId ?? undefined;
    const globalDelta = explicitRecDeltaFor(effectiveLocationMongoId ?? locId);
    const finalPostLocationCount =
      fetchedPostLocationCount !== null ? fetchedPostLocationCount : extracted.recommendations;
    /** List-opened modal: keep same explicit count as the card; search-by-name breakdown can disagree.
     *  The v2 breakdown is fetched by _id together with the avatars, so it wins over the card. */
    const hasListSlotExplicitHint =
      typeof (locationData as { list_slot_explicit_recommendations?: number })
        .list_slot_explicit_recommendations === 'number';
    const base =
      fetchedExplicitRecCount !== null && (recCountsFromDetails || !hasListSlotExplicitHint)
        ? fetchedExplicitRecCount
        : extracted.explicitRecommendations;
    const finalExplicitRecs = Math.max(0, base + globalDelta);
    return {
      ...extracted,
      recommendations: finalPostLocationCount,
      explicitRecommendations: finalExplicitRecs,
      recommendationAvatars: fetchedRecAvatars
        ? fetchedRecAvatars
        : (extracted.recommendationAvatars ?? []).map((url) => ({ userId: '', avatarUrl: url })),
      website: fetchedWebsite !== undefined ? fetchedWebsite : extracted.website,
    };
  }, [
    locationData,
    locationId,
    fetchedWebsite,
    fetchedExplicitRecCount,
    fetchedPostLocationCount,
    recCountsFromDetails,
    fetchedRecAvatars,
    explicitRecDeltaFor,
    effectiveLocationMongoId,
  ]);

  const currentlyRecommended = useMemo(
    () => !!(effectiveLocationMongoId && isRecommended(effectiveLocationMongoId)),
    [isRecommended, effectiveLocationMongoId],
  );

  const displayExplicitRecommendations = useMemo(
    () => Math.max(extractedLocationData.explicitRecommendations, currentlyRecommended ? 1 : 0),
    [extractedLocationData.explicitRecommendations, currentlyRecommended],
  );

  const saveMongoId = useMemo(() => {
    return effectiveLocationMongoId && /^[a-f\d]{24}$/i.test(effectiveLocationMongoId)
      ? effectiveLocationMongoId
      : null;
  }, [effectiveLocationMongoId]);

  const isLocationSavedByMe = !!(saveMongoId && bookmarkState.userSavedLocationIds.has(saveMongoId));
  const isLocationSaveBusy = !!(saveMongoId && bookmarkState.saveBusyIds.has(saveMongoId));

  // ── Photos ──────────────────────────────────────────────────────────────────
  const allPhotoUrls = useMemo(() => {
    const urls = locationData
      ? extractPhotosFromLocation(locationData as Parameters<typeof extractPhotosFromLocation>[0], 600)
      : [];
    return urls.length > 0 || !extractedLocationData.image ? urls : [extractedLocationData.image];
  }, [locationData, extractedLocationData.image]);

  // ── Category / stay detection ───────────────────────────────────────────────
  const isStayCategory = useMemo(
    () =>
      isStayPlace({
        gt_category: locationData?.gt_category,
        category_name: locationData?.category_name,
        google_types: locationData?.google_types ?? locationData?.google_places_data?.types,
      }),
    [locationData],
  );

  // Mirrors validatePlaceForStayBooking: whitelabel books Nuitee hotels by hotel id alone,
  // other places need a Google place id or coordinates.
  const canBook = useMemo(() => {
    if (!isStayCategory) return false;
    if (nuiteeHotelId) return true;
    const googlePlaceId =
      locationData?.google_place_id || locationData?.google_places_data?.place_id;
    const lat = locationData?.geoposition?.coordinates?.[1];
    const lng = locationData?.geoposition?.coordinates?.[0];
    return !!(googlePlaceId || (lat && lng));
  }, [isStayCategory, nuiteeHotelId, locationData]);

  const canShowExperiences = useMemo(() => {
    if (!isGygExperiencesEnabled()) {
      return false;
    }
    return canShowGygForLocation(locationData as GygLocationInput | undefined);
  }, [locationData]);

  const experiencesSearchQuery = useMemo(
    () => buildGygSearchQueryFromLocation(locationData as GygLocationInput | undefined),
    [locationData],
  );

  const experiencesGooglePlaceId = useMemo(
    () =>
      locationData?.google_place_id ?? locationData?.google_places_data?.place_id ?? undefined,
    [locationData?.google_place_id, locationData?.google_places_data?.place_id],
  );

  // ── Website fetch ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen || !locationData || !isLoggedIn || locationData.website) {
      setFetchedWebsite(undefined);
      return;
    }
    const googlePlaceId = locationData.google_place_id;
    const locationName = locationData.name;
    if (!googlePlaceId || !locationName) { setFetchedWebsite(undefined); return; }
    const ctrl = new AbortController();
    const cityAddress = locationData.a3_intl_address || locationData.address || '';
    fetchWebsiteIfMissing(locationData as RawLocationData, locationName, cityAddress)
      .then((updated) => { if (!ctrl.signal.aborted && updated?.website) setFetchedWebsite(updated.website); })
      .catch((err) => { if (!ctrl.signal.aborted) logError('[CombinedModal] Error fetching website:', err); });
    return () => ctrl.abort();
  }, [isOpen, locationData, isLoggedIn]);

  // When switching place while modal stays open, clear stale fetched data.
  const prevModalLocationKeyRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!isOpen) {
      prevModalLocationKeyRef.current = undefined;
      setResolvedMongoId(null);
      setIsResolvingMongoId(false);
      return;
    }
    const key = `${locationId ?? ''}|${(locationData as Record<string, unknown> | undefined)?._id ?? ''}`;
    if (prevModalLocationKeyRef.current !== undefined && prevModalLocationKeyRef.current !== key) {
      setFetchedRecAvatars(null);
      setResolvedMongoId(null);
      setIsResolvingMongoId(false);
    }
    prevModalLocationKeyRef.current = key;
  }, [isOpen, locationId, locationData]);

  // ── Place details, rec counts + recommender avatars ─────────────────────────
  // GET /api/v2/location/:id is the one request on open: ai_summary, city_name, menu / OpenTable
  // links, plus (signed in) recommendations[].user_small_avatar_url, rec_count_breakdown and
  // my_visit. The first open also asks the backend to write ai_summary.
  // Name search is only a fallback: no Mongo _id yet (itinerary markers), or no breakdown on v2;
  // its match _id then loads the details.
  const [placeDetails, setPlaceDetails] = useState<Location | null>(null);
  const searchName = locationData?.name;
  const searchAddress = locationData?.a3_intl_address || locationData?.address || '';
  const searchGooglePlaceId = locationData?.google_place_id;

  useEffect(() => {
    setPlaceDetails(null);
    setRecCountsFromDetails(false);
    if (!isLoggedIn) {
      setFetchedExplicitRecCount(null);
      setFetchedPostLocationCount(null);
    }
    if (!isOpen) return;

    const ctrl = new AbortController();

    const applyBreakdown = (bd: NonNullable<Location['rec_count_breakdown']>) => {
      setFetchedPostLocationCount(bd.post_locations ?? null);
      setFetchedExplicitRecCount(bd.explicit_recommendations ?? null);
    };

    const searchCountsByName = (loadDetailsForMatch: boolean) => {
      if (!searchName) return;
      const cityMatch = searchAddress.match(/,([^,]+),/);
      const city = cityMatch ? cityMatch[1].trim() : undefined;
      searchLocationByName({ locationName: searchName, city })
        .then((locs) => {
          if (ctrl.signal.aborted) return;
          const match = locs.find(
            (l) =>
              (searchGooglePlaceId && l.google_place_id === searchGooglePlaceId) ||
              l.name?.toLowerCase() === searchName.toLowerCase(),
          );
          if (match?.rec_count_breakdown) applyBreakdown(match.rec_count_breakdown);
          if (loadDetailsForMatch && match?._id && isMongoObjectIdString(match._id)) {
            loadDetails(match._id, false);
          }
        })
        .catch((err) => { if (!ctrl.signal.aborted) logError('[CombinedModal] Error fetching rec counts:', err); });
    };

    const loadDetails = (locId: string, searchIfNoBreakdown: boolean) => {
      getLocationById(locId)
        .then((loc) => {
          if (ctrl.signal.aborted) return;
          setPlaceDetails(loc);
          if (!isLoggedIn) return;
          // recommendations[] has every row; keep explicit/enriched to match rec_count_breakdown
          const avatars = getRecommenderAvatarsWithIdsFromLocationLike(loc, true);
          if (avatars.length > 0) setFetchedRecAvatars(avatars);
          if (loc.my_visit !== undefined) {
            setVisitStatus({ rating: loc.my_visit?.rating ?? null, note: loc.my_visit?.note ?? '' });
          }
          if (loc.rec_count_breakdown) {
            applyBreakdown(loc.rec_count_breakdown);
            setRecCountsFromDetails(true);
          } else if (searchIfNoBreakdown) searchCountsByName(false);
        })
        .catch((err) => {
          if (ctrl.signal.aborted) return;
          logError('[CombinedModal] Error fetching v2 place details:', err);
          if (searchIfNoBreakdown && isLoggedIn) searchCountsByName(false);
        });
    };

    if (effectiveLocationMongoId && isMongoObjectIdString(effectiveLocationMongoId)) {
      loadDetails(effectiveLocationMongoId, true);
    } else if (isLoggedIn) {
      searchCountsByName(true);
    }

    return () => ctrl.abort();
  }, [isOpen, isLoggedIn, effectiveLocationMongoId, searchName, searchAddress, searchGooglePlaceId]);

  // ── Trip card actions ───────────────────────────────────────────────────────
  const handleTripCardClick = useCallback((postId: string) => { onClose(); router(`/post/${postId}`); }, [onClose, router]);

  const handleTripCardSaveClick = useCallback((postId: string) => {
    if (!user?._id) return;
    const isSaved = savedMap[postId];
    const updatePosts = (fn: (prev: Post[]) => Post[]) => setAssociatedPosts((prev) => fn(prev));
    if (isSaved) {
      setSavedMap((prev) => ({ ...prev, [postId]: false }));
      void handleUnsave(postId, updatePosts as Parameters<typeof handleUnsave>[1]);
    } else {
      setSavedMap((prev) => ({ ...prev, [postId]: true }));
      void handleSave(postId, updatePosts as Parameters<typeof handleSave>[1]);
    }
  }, [user?._id, savedMap, handleSave, handleUnsave]);

  const handleTripCardShareClick = useCallback((postId: string) => {
    const post = associatedPosts.find((p) => p._id === postId);
    if (!post) return;
    void sharePost(post._id, post.name || 'Post', post.post_type);
  }, [associatedPosts, sharePost]);

  // ── Place card data (for SaveToOverlay) ─────────────────────────────────────
  const placeCardData = useMemo((): PlaceCardData | null => {
    if (!locationData) return null;
    const isNuiteeHotel = !!locationData.nuitee_hotel_id;
    const googlePlaceId = isNuiteeHotel
      ? (enrichedIds?.hotel_google_place_id ?? null)
      : (locationData.google_place_id ?? null);
    const lat = locationData.geoposition?.coordinates?.[1];
    const lng = locationData.geoposition?.coordinates?.[0];
    return {
      id: locationData._id || locationId || '',
      title: locationData.name || 'Unknown location',
      address: locationData.a3_intl_address || locationData.address || '',
      cost: locationData.cost || locationData.budget,
      notes: locationData.description || '',
      description: locationData.description || '',
      recommendations: extractedLocationData.recommendations,
      recommendationAvatars: (extractedLocationData.recommendationAvatars || []).map((a) => a.avatarUrl),
      explicitRecommendations: displayExplicitRecommendations,
      locationId: effectiveLocationMongoId ?? extractedLocationData.locationId,
      googlePlaceId,
      imageUrls: locationData.main_photo
        ? [locationData.main_photo]
        : ((locationData as Record<string, unknown>).image_urls as string[] | undefined)?.length
          ? (locationData as Record<string, unknown>).image_urls as string[]
          : [],
      latitude: lat,
      longitude: lng,
      locationType: isNuiteeHotel ? 'Stay' : undefined,
    };
  }, [locationData, locationId, extractedLocationData, displayExplicitRecommendations, enrichedIds, effectiveLocationMongoId]);

  // ── Save to itinerary / list ────────────────────────────────────────────────
  const fetchUserItineraries = useCallback(async () => {
    if (!isLoggedIn) return;
    setIsLoadingItineraries(true);
    try {
      const result = await getUserItineraries();
      setUserItineraries(result.posts);
    } catch (err) {
      logError('[CombinedModal] Error fetching itineraries:', err);
      toast.error('Failed to load itineraries.');
    } finally {
      setIsLoadingItineraries(false);
    }
  }, [isLoggedIn]);

  const fetchUserLists = useCallback(async () => {
    if (!isLoggedIn) return;
    setIsLoadingLists(true);
    try {
      const result = await getUserLists();
      setUserLists(result.posts);
    } catch (err) {
      logError('[CombinedModal] Error fetching lists:', err);
    } finally {
      setIsLoadingLists(false);
    }
  }, [isLoggedIn]);

  const handlePlaceSaveClick = useCallback(() => {
    if (!isLoggedIn) {
      WebAnalytics.trackLoggedOutInteraction(EVENT_LOGGED_OUT_PLACE_SAVE_CLICKED, 'Place Modal', {
        place_id: locationId ?? null,
        place_title: locationData?.name ?? null,
        auth_cta_shown: true,
      });
      requireAuth('save', () => {});
      return;
    }
    if (!placeCardData) return;
    WebAnalytics.trackContentInteraction(EVENT_PLACE_SAVE_TO_ITINERARY_CLICKED, {
      placeId: placeCardData.id,
      placeTitle: placeCardData.title,
    });
    setSelectedPlaceForSave(placeCardData);
    setIsSaveToOverlayOpen(true);
    void fetchUserItineraries();
    void fetchUserLists();
  }, [isLoggedIn, requireAuth, placeCardData, locationId, locationData, fetchUserItineraries, fetchUserLists]);

  const handleLocationSaveToggle = useCallback(async (): Promise<'saved' | 'unsaved' | 'error'> => {
    if (!isLoggedIn) {
      requireAuth('save', () => {});
      return 'error';
    }
    if (!placeCardData || !saveMongoId) {
      if (isResolvingMongoId || isEnriching) {
        toast.error('Place is still loading. Please try again in a moment.');
      }
      return 'error';
    }
    return syncLocationBookmarkToggle(placeCardData, undefined, saveMongoId);
  }, [isLoggedIn, requireAuth, placeCardData, saveMongoId, syncLocationBookmarkToggle, isResolvingMongoId, isEnriching]);

  const handleSaveToItinerary = useCallback(async (itineraryId: string) => {
    if (!selectedPlaceForSave) return;
    try {
      await addPlaceToItinerary(itineraryId, selectedPlaceForSave);
      const itinerary = userItineraries.find((it) => it.id === itineraryId);
      WebAnalytics.trackContentInteraction(EVENT_PLACE_SAVED_TO_ITINERARY, {
        placeId: selectedPlaceForSave.id,
        placeTitle: selectedPlaceForSave.title,
        itineraryId,
        itineraryName: itinerary?.name || 'Unknown',
      });
    } catch (err) {
      logError('[CombinedModal] Error saving to itinerary:', err);
      toast.error('Failed to add place to itinerary.');
    }
  }, [selectedPlaceForSave, userItineraries]);

  const handleSaveToList = useCallback(async (listId: string) => {
    if (!selectedPlaceForSave) return;
    try {
      await addPlaceToList(listId, selectedPlaceForSave);
    } catch (err) {
      logError('[CombinedModal] Error saving to list:', err);
      toast.error('Failed to add place to list.');
    }
  }, [selectedPlaceForSave]);

  const handleCreateTrip = useCallback(async () => {
    if (!isLoggedIn) { requireAuth('save', () => {}); return; }
    if (!placeCardData) return;
    WebAnalytics.track(EVENT_PLACE_CREATE_TRIP_CLICKED, {
      locationId: locationId ?? null,
      locationName: placeCardData.title,
      isNuiteeHotel: !!locationData?.nuitee_hotel_id,
    });
    setIsCreatingTrip(true);
    try {
      const created = await createItineraryWithPlace('New Itinerary', placeCardData, true);
      WebAnalytics.trackContentCreation(EVENT_PLACE_CREATE_TRIP_SUCCEEDED, {
        locationId: locationId ?? null,
        locationName: placeCardData.title,
        itineraryId: created._id,
      });
      onClose();
      router(`/itinerary/${created._id}`);
    } catch (err) {
      logError('[CombinedModal] Error creating itinerary:', err);
      toast.error('Failed to create itinerary. Please try again.');
    } finally {
      setIsCreatingTrip(false);
    }
  }, [isLoggedIn, requireAuth, placeCardData, locationId, locationData, onClose, router]);

  // ── Recommend ───────────────────────────────────────────────────────────────
  // ── I've been here ──────────────────────────────────────────────────────────
  const handleVisitStatusChange = useCallback((next: VisitStatus) => {
    if (!isLoggedIn) {
      requireAuth('recs', () => {}, {
        title: 'Sign up to save places you’ve been',
        description: 'Create an account to rate the places you’ve visited and help other travelers.',
      });
      return;
    }
    const locId = effectiveLocationMongoId;
    if (!locId || !isMongoObjectIdString(locId)) {
      toast.error('Place is still loading. Please try again in a moment.');
      return;
    }
    const previous = visitStatus;
    const becameLoved = next.rating === 'loved' && previous.rating !== 'loved';
    setVisitStatus(next);
    // rating null deletes the visit; the backend recommends the place when it changes to "loved"
    putLocationVisit(locId, next.rating ? { rating: next.rating, note: next.note } : { rating: null })
      .then(() => {
        if (!becameLoved || currentlyRecommended) return;
        addRecommendation(locId);
        bumpExplicitRecDelta(locId, 1);
        refreshGtReviewsData();
        const myUserId = user?._id ? String(user._id) : '';
        if (myUserId) {
          const myAvatarUrl = (user as Record<string, unknown> | null)?.small_avatar_url as string | undefined;
          setFetchedRecAvatars((prev) => {
            const entry: RecommenderAvatar = { userId: myUserId, avatarUrl: myAvatarUrl ?? '' };
            if (!prev) return [entry];
            if (prev.some((a) => a.userId === myUserId)) return prev;
            return [...prev, entry];
          });
        }
      })
      .catch((err) => {
        setVisitStatus(previous);
        logError('[CombinedModal] Error saving visit', err);
        toast.error('Failed to save your visit.');
      });
  }, [
    isLoggedIn,
    requireAuth,
    effectiveLocationMongoId,
    visitStatus,
    currentlyRecommended,
    addRecommendation,
    bumpExplicitRecDelta,
    refreshGtReviewsData,
    user,
  ]);

  const handleOpenEnrichedRecommendModal = useCallback(() => {
    requireAuth(
      'recs',
      () => {
        const raw = locationData as Record<string, unknown> | undefined;
        if (!raw || typeof raw !== 'object') {
          toast.error('Could not open recommendations: missing place data.');
          return;
        }
        const mongoId =
          effectiveLocationMongoId && isMongoObjectIdString(effectiveLocationMongoId)
            ? effectiveLocationMongoId
            : getCanonicalLocationMongoId({
                _id: raw._id,
                location_id: raw.location_id,
                locationId: locationId ?? extractedLocationData.locationId,
              });
        if (!mongoId || !isMongoObjectIdString(mongoId)) {
          toast.error('Could not resolve this place for recommendations.');
          return;
        }
        const locationObj = new Location({ ...raw, _id: mongoId } as Record<string, unknown>);

        // If user has already recommended this place, fetch their existing enrichment
        // data so the modal pre-fills in edit mode.
        if (currentlyRecommended) {
          getMyRecommendationForLocation(mongoId)
            .then((existingDoc) => {
              setEnrichedRecommendInitialDoc(existingDoc ?? null);
              setEnrichedRecommendLocation(locationObj);
            })
            .catch(() => {
              // Fall through to create mode on any fetch error
              setEnrichedRecommendInitialDoc(null);
              setEnrichedRecommendLocation(locationObj);
            });
        } else {
          setEnrichedRecommendInitialDoc(null);
          setEnrichedRecommendLocation(locationObj);
        }
      },
      {
        title: 'Sign up to recommend places',
        description: 'Create an account to recommend places you love and help other travelers discover them.',
      },
      { context: 'place_modal' },
    );
  }, [
    requireAuth,
    locationData,
    locationId,
    extractedLocationData.locationId,
    currentlyRecommended,
    effectiveLocationMongoId,
  ]);

  // ── Share ───────────────────────────────────────────────────────────────────
  const isValidMongoId = !!(effectiveLocationMongoId && /^[a-f\d]{24}$/i.test(effectiveLocationMongoId));

  const handleLocationShareClick = useCallback(() => {
    requireAuth('share', () => {
      WebAnalytics.trackContentInteraction(EVENT_LOCATION_SHARE_CLICKED, {
        locationId: effectiveLocationMongoId ?? extractedLocationData.locationId,
        locationName: extractedLocationData.name,
      });
      debug('[CombinedContentPlaceModal] Opening location ShareModal', {
        locationId: effectiveLocationMongoId ?? extractedLocationData.locationId,
      });
      setIsLocationShareModalOpen(true);
    });
  }, [requireAuth, effectiveLocationMongoId, extractedLocationData.locationId, extractedLocationData.name]);

  const recordAttributedPlaceBookingClick = useCallback(() => {
    const resolvedLocationId = effectiveLocationMongoId ?? locationId;
    if (
      validAttributionPostId &&
      resolvedLocationId &&
      /^[a-f\d]{24}$/i.test(resolvedLocationId)
    ) {
      void recordBookingClick({
        postId: validAttributionPostId,
        locationId: resolvedLocationId,
        referralCreatorId: getReferralCreatorIdForApi(),
      });
    }
  }, [effectiveLocationMongoId, locationId, validAttributionPostId]);

  const experiencesPlaceId = useMemo(() => {
    const id = effectiveLocationMongoId ?? locationId;
    return id && /^[a-f\d]{24}$/i.test(id) ? id : undefined;
  }, [effectiveLocationMongoId, locationId]);

  const stay22Location = useMemo(
    () =>
      resolveStay22LocationFromPlace({
        locationData,
        fallbackAddress: extractedLocationData.address,
        fallbackName: extractedLocationData.name,
      }),
    [locationData, extractedLocationData.address, extractedLocationData.name],
  );

  const stay22GooglePlaceId = useMemo(() => {
    return (
      (nuiteeHotelId ? enrichedIds?.google_place_id : null) ||
      locationData?.google_place_id ||
      locationData?.google_places_data?.place_id ||
      undefined
    );
  }, [locationData, enrichedIds, nuiteeHotelId]);

  /** GT Exclusive row is always inline (unless admins book via the in-app API); OTA rows need Stay22. */
  const showInlineStayBookingOptions = !ENABLE_IN_APP_API_BOOKING || !isUserAdmin();
  const showStay22Vendors = isStay22Enabled();

  const hotelWidgetImpression = useMemo(() => {
    const mongoId = effectiveLocationMongoId ?? locationId;
    const contentType = listCtx?.listPost
      ? ('list' as const)
      : pathname.includes('/itinerary/')
        ? ('itinerary' as const)
        : pathname.includes('/list/')
          ? ('list' as const)
          : undefined;

    return {
      ...(user?._id && { user_id: String(user._id) }),
      ...(validAttributionPostId && { post_id: validAttributionPostId }),
      ...(mongoId && /^[a-f\d]{24}$/i.test(mongoId) && { place_id: mongoId }),
      ...(stay22GooglePlaceId && { google_place_id: stay22GooglePlaceId }),
      ...(extractedLocationData.name && { location_name: extractedLocationData.name }),
      ...(contentType && { content_type: contentType }),
    };
  }, [
    effectiveLocationMongoId,
    locationId,
    listCtx?.listPost,
    pathname,
    user?._id,
    validAttributionPostId,
    stay22GooglePlaceId,
    extractedLocationData.name,
  ]);

  const validatePlaceForStayBooking = useCallback((): boolean => {
    const placeId =
      (nuiteeHotelId ? enrichedIds?.google_place_id : null) ||
      locationData?.google_place_id ||
      locationData?.google_places_data?.place_id;
    const latitude = locationData?.geoposition?.coordinates?.[1];
    const longitude = locationData?.geoposition?.coordinates?.[0];
    if (!placeId && !nuiteeHotelId && (!latitude || !longitude)) {
      setStayPlaceError(MISSING_PLACE_ERROR);
      return false;
    }
    return true;
  }, [locationData, nuiteeHotelId, enrichedIds]);

  const trackPlaceStayBookingIntent = useCallback(
    (buttonAction: 'book' | 'check_availability' = 'book') => {
      const contentType = listCtx?.listPost
        ? ('list' as const)
        : pathname.includes('/itinerary/')
          ? ('itinerary' as const)
          : pathname.includes('/list/')
            ? ('list' as const)
            : undefined;
      WebAnalytics.trackHighIntentTrip({
        button_action: buttonAction,
        locationId: locationId ?? null,
        locationName: locationData?.name ?? null,
        hotelId: nuiteeHotelId ?? null,
        source: 'place_modal',
        ...(validAttributionPostId && { post_id: validAttributionPostId }),
        ...(contentType && { content_type: contentType }),
      });
      recordAttributedPlaceBookingClick();
    },
    [
      listCtx?.listPost,
      pathname,
      locationId,
      locationData?.name,
      nuiteeHotelId,
      validAttributionPostId,
      recordAttributedPlaceBookingClick,
    ],
  );

  const openPlaceStayNuitee = useCallback(
    (searchParams: PlaceModalSearchParams) => {
      WebAnalytics.track(EVENT_PLACE_STAY_BOOK_CHOICE_SELECTED, {
        choice: 'nuitee',
        source: 'place_modal',
        ...(validAttributionPostId && { post_id: validAttributionPostId }),
      });
      const placeId =
        (nuiteeHotelId ? enrichedIds?.google_place_id : null) ||
        locationData?.google_place_id ||
        locationData?.google_places_data?.place_id;
      openWhitelabelForPlace({
        placeId: placeId ?? '',
        hotelId: nuiteeHotelId || undefined,
        searchParams,
        postId: validAttributionPostId,
        googlePlaceId: nuiteeHotelGooglePlaceId ?? placeId ?? undefined,
      });
    },
    [
      enrichedIds,
      locationData,
      nuiteeHotelId,
      nuiteeHotelGooglePlaceId,
      openWhitelabelForPlace,
      validAttributionPostId,
    ],
  );

  const handlePlaceStayBookNuitee = useCallback(
    (searchParams: PlaceModalSearchParams) => {
      if (!validatePlaceForStayBooking()) return;
      trackPlaceStayBookingIntent('book');
      if (!isLoggedIn) {
        requireAuth('book', () => openPlaceStayNuitee(searchParams), {
          title: "These rates aren't public 👀",
          description:
            'Create a free account to view exclusive pricing and credit the creator who inspired this trip.',
        });
        return;
      }
      openPlaceStayNuitee(searchParams);
    },
    [
      validatePlaceForStayBooking,
      trackPlaceStayBookingIntent,
      isLoggedIn,
      requireAuth,
      openPlaceStayNuitee,
    ],
  );

  const handlePlaceStayBookStay22Vendor = useCallback(
    (provider: Stay22AllezProvider, searchParams: PlaceModalSearchParams) => {
      if (!validatePlaceForStayBooking()) return;
      trackPlaceStayBookingIntent('book');
      WebAnalytics.track(EVENT_PLACE_STAY_BOOK_CHOICE_SELECTED, {
        choice: 'stay22',
        open_type: 'allez',
        allez_provider: provider,
        source: 'place_modal',
        ...(validAttributionPostId && { post_id: validAttributionPostId }),
      });
      openStay22DeepLink({
        userId: user?._id ? String(user._id) : undefined,
        postId: validAttributionPostId,
        placeId: experiencesPlaceId,
        googlePlaceId: stay22GooglePlaceId,
        surface: 'place_modal',
        locationName: extractedLocationData.name,
        lat: stay22Location.lat,
        lng: stay22Location.lng,
        address: stay22Location.address,
        checkIn: searchParams.checkIn,
        checkOut: searchParams.checkOut,
        adults: searchParams.adults,
        provider,
      });
    },
    [
      validatePlaceForStayBooking,
      trackPlaceStayBookingIntent,
      validAttributionPostId,
      openStay22DeepLink,
      user?._id,
      experiencesPlaceId,
      stay22GooglePlaceId,
      extractedLocationData.name,
      stay22Location,
    ],
  );

  // ── Booking ─────────────────────────────────────────────────────────────────
  const handleBookNow = useCallback((searchParams?: PlaceModalSearchParams) => {
    if (!isLoggedIn) {
      requireAuth('book', () => {}, {
        title: "These rates aren't public 👀",
        description: "Create a free account to view exclusive pricing and credit the creator who inspired this trip.",
      });
      return;
    }
    const buttonAction = searchParams?.button_action;
    debug('[CombinedContentPlaceModal] handleBookNow called', {
      hasLocationData: !!locationData,
      searchParams,
      buttonAction,
    });
    const placeId =
      (nuiteeHotelId ? enrichedIds?.google_place_id : null) ||
      locationData?.google_place_id ||
      locationData?.google_places_data?.place_id;
    const latitude = locationData?.geoposition?.coordinates?.[1];
    const longitude = locationData?.geoposition?.coordinates?.[0];
    if (!placeId && !nuiteeHotelId && (!latitude || !longitude)) {
      setStayPlaceError(MISSING_PLACE_ERROR);
      return;
    }
    const contentType = listCtx?.listPost
      ? ('list' as const)
      : pathname.includes('/itinerary/')
        ? ('itinerary' as const)
        : pathname.includes('/list/')
          ? ('list' as const)
          : undefined;
    WebAnalytics.trackHighIntentTrip({
      button_action: buttonAction === 'check_availability' ? 'check_availability' : 'book',
      locationId: locationId ?? null,
      locationName: locationData?.name ?? null,
      hotelId: nuiteeHotelId ?? null,
      source: 'place_modal',
      ...(validAttributionPostId && { post_id: validAttributionPostId }),
      ...(contentType && { content_type: contentType }),
    });
    recordAttributedPlaceBookingClick();
    const whitelabelParams: PlaceModalSearchParams | undefined =
      searchParams?.checkIn != null
        ? {
            checkIn: searchParams.checkIn,
            checkOut: searchParams.checkOut,
            adults: searchParams.adults,
            rooms: searchParams.rooms,
          }
        : undefined;
    if (!ENABLE_IN_APP_API_BOOKING || !isUserAdmin()) {
      openWhitelabelForPlace({
        placeId: placeId ?? '',
        hotelId: nuiteeHotelId || undefined,
        searchParams: whitelabelParams,
        postId: validAttributionPostId,
        googlePlaceId: nuiteeHotelGooglePlaceId ?? placeId ?? undefined,
      });
      return;
    }
    setPlaceModalSearchParams(whitelabelParams ?? null);
    const resolvedLocationId = effectiveLocationMongoId ?? locationId;
    setBookingContext({
      placeId: placeId || undefined,
      hotelId: nuiteeHotelId || undefined,
      latitude,
      longitude,
      postId: validAttributionPostId,
      locationId:
        resolvedLocationId && /^[a-f\d]{24}$/i.test(resolvedLocationId)
          ? resolvedLocationId
          : undefined,
    });
    setIsBookingModalOpen(true);
  }, [
    locationData,
    locationId,
    nuiteeHotelId,
    nuiteeHotelGooglePlaceId,
    enrichedIds,
    isLoggedIn,
    requireAuth,
    openWhitelabelForPlace,
    validAttributionPostId,
    effectiveLocationMongoId,
    listCtx?.listPost,
    pathname,
    recordAttributedPlaceBookingClick,
  ]);

  const handleBrowseExperiences = useCallback(() => {
    const searchQuery = buildGygSearchQueryFromLocation(locationData as GygLocationInput | undefined);
    if (!searchQuery) {
      toast.error('Unable to find experiences for this place.');
      return;
    }
    const googlePlaceId =
      locationData?.google_place_id ?? locationData?.google_places_data?.place_id ?? undefined;
    const gygParams = {
      searchQuery,
      userId: user?._id ? String(user._id) : undefined,
      postId: validAttributionPostId,
      placeId: validAttributionPostId ? undefined : experiencesPlaceId,
      googlePlaceId,
      surface: 'place_modal' as const,
      locationName: locationData?.name ?? extractedLocationData.name,
    };
    openGygDeepLink(gygParams);
  }, [
    locationData,
    user?._id,
    validAttributionPostId,
    experiencesPlaceId,
    extractedLocationData.name,
    openGygDeepLink,
  ]);

  const handleSelectAPI = useCallback(() => { setIsBookingMethodSelectionOpen(false); setIsBookingModalOpen(true); }, []);

  const handleSelectWhitelabel = useCallback(() => {
    setIsBookingMethodSelectionOpen(false);
    const placeId = bookingContext?.placeId || (nuiteeHotelId ? enrichedIds?.google_place_id : null) || locationData?.google_place_id || locationData?.google_places_data?.place_id;
    openWhitelabelForPlace({
      placeId: placeId ?? '',
      hotelId: bookingContext?.hotelId || nuiteeHotelId || undefined,
      searchParams: placeModalSearchParams ?? undefined,
      postId: bookingContext?.postId,
      googlePlaceId: nuiteeHotelGooglePlaceId ?? placeId ?? undefined,
    });
  }, [bookingContext, locationData, nuiteeHotelId, nuiteeHotelGooglePlaceId, enrichedIds, placeModalSearchParams, openWhitelabelForPlace]);

  // ── Trip card prop mapper ────────────────────────────────────────────────────
  const mapPostToTripCardProps = useCallback((post: Post) => ({
    id: post._id,
    title: post.name || 'Untitled Trip',
    destination: getPostDestinationLabel(post),
    destinationCount: getTripCardDestinationCount(post),
    heroImages: extractAllPostPhotos(post),
    authorName: post.displayUser?.name || post.user?.name || 'Anonymous',
    authorAvatar: post.displayUser?.large_avatar_url || post.user?.large_avatar_url || '',
    authorAvatarSecondary: post.displayUser?.small_avatar_url || post.user?.small_avatar_url || '',
    authorAvatarTertiary: post.user?.avatar_url || '',
    authorAvatarQuaternary: post.user?.profile_image_url || '',
    authorId: post.user?._id || '',
    tripTypes: (post.tag_list || []).filter((tag: string) => tag !== 'Budget' && tag !== 'budget'),
    travelWithTypes: post.travel_with_types || [],
    budget: post.budget ?? null,
    savesCount: post.saves?.length || 0,
    sharesCount: post.lifetime_unique_sharer_count ?? 0,
    isFollowing: post.user?.is_following || false,
    isSaved: savedMap[post._id] ?? post.is_saved ?? false,
    postType: post.post_type as 'List' | 'Itinerary',
    placeCount: post.postLocations?.length || undefined,
    dayCount: post.post_type === 'Itinerary' ? post.itinerary_data?.day_count : undefined,
    remixParentCreatorId: post.remix_parent_creator_id,
    remixParentCreatorName: post.remix_parent_creator_name,
    remixParentPostId: post.remix_parent_post_id,
    remixHasAttribution: post.remix_has_attribution,
    onClick: handleTripCardClick,
    onSaveClick: handleTripCardSaveClick,
    onShareClick: handleTripCardShareClick,
  }), [savedMap, handleTripCardClick, handleTripCardSaveClick, handleTripCardShareClick]);

  // ── Booking portals (always rendered for cleanup) ───────────────────────────
  const bookingModals = typeof window !== 'undefined' && (
    <>
      {isBookingMethodSelectionOpen && createPortal(
        <BookingMethodSelectionModal
          isOpen={isBookingMethodSelectionOpen}
          onClose={() => setIsBookingMethodSelectionOpen(false)}
          onSelectAPI={handleSelectAPI}
          onSelectWhitelabel={handleSelectWhitelabel}
          hotelName={extractedLocationData?.name || locationData?.name || 'Hotel'}
        />,
        document.body,
      )}
      {isBookingModalOpen && bookingContext && createPortal(
        <BookingModal
          isOpen={isBookingModalOpen}
          onClose={() => {
            setIsBookingModalOpen(false);
            setBookingContext(null);
            setPlaceModalSearchParams(null);
            setIsBookingMethodSelectionOpen(false);
            onClose();
          }}
          context={bookingContext}
          initialSearchParams={placeModalSearchParams || undefined}
        />,
        document.body,
      )}
      <BookingSsoBlockedModal
        isOpen={!!bookingBlockedLoginUrl}
        onClose={clearBookingBlockedState}
        loginUrl={bookingBlockedLoginUrl}
      />
      <BookingErrorModal error={shownBookingError} onClose={closeBookingError} />
      <BookingSsoBlockedModal
        isOpen={!!experiencesBlockedUrl}
        onClose={clearExperiencesBlockedState}
        loginUrl={experiencesBlockedUrl}
        variant="gyg"
      />
    </>
  );

  if (!isOpen && !isBookingMethodSelectionOpen && !isBookingModalOpen) {
    return bookingModals;
  }
  if (isOpen && !locationId && !locationData) return null;

  const modalContent = (
    <ModalOverlay
      onClose={onClose}
      className={overlayClassName}
      position="right-side"
      presentation={isMobileViewport ? 'fullscreen' : 'sheet'}
    >
      <div
        data-gt-place-modal
        className={MODAL_PANEL_ROOT}
        onClick={(e) => e.stopPropagation()}
      >
        <PlaceModalToolbar
          isLocationSaved={isLocationSavedByMe}
          isLocationSaveBusy={isLocationSaveBusy || isResolvingMongoId}
          onToggleLocationSave={handleLocationSaveToggle}
          isShareEnabled={isValidMongoId}
          onShareClick={handleLocationShareClick}
          visitStatus={visitStatus}
          onVisitStatusChange={handleVisitStatusChange}
          onClose={onClose}
        />

        {view === 'photos' ? (
          <div className="flex flex-col gap-4 px-6 pb-6 max-md:px-4">
            <button
              type="button"
              onClick={() => handleViewChange('details')}
              className="inline-flex items-center gap-1.5 self-start border-none bg-transparent p-0 font-manrope text-sm font-semibold leading-5 tracking-[0.02em] text-[#252528] hover:opacity-70"
            >
              <ArrowBackIcon sx={{ fontSize: 18 }} aria-hidden />
              {extractedLocationData.name}
            </button>
            <PlaceModalPhotosTab photoUrls={allPhotoUrls} />
          </div>
        ) : (
          <PlaceModalBody
            locationData={locationData}
            name={extractedLocationData.name}
            address={extractedLocationData.address}
            description={
              placeDetails?.ai_summary ||
              locationData?.ai_summary ||
              extractedLocationData.description ||
              getGoogleEditorialSummary(locationData?.google_places_data as Record<string, unknown> | undefined) ||
              undefined
            }
            phone={extractedLocationData.phone}
            website={extractedLocationData.website}
            cityName={placeDetails?.city_name ?? locationData?.city_name ?? undefined}
            country={placeDetails?.country || locationData?.country || undefined}
            menuUrl={placeDetails?.menu_url ?? locationData?.menu_url}
            reservationUrl={placeDetails?.reservation_url ?? locationData?.reservation_url}
            openTableRid={placeDetails?.opentable_rid ?? locationData?.opentable_rid}
            photoUrls={allPhotoUrls}
            onPhotosClick={() => handleViewChange('photos')}
            explicitRecommendations={displayExplicitRecommendations}
            recommendationAvatars={extractedLocationData.recommendationAvatars || []}
            tipsLocationId={effectiveLocationMongoId ?? locationId}
            tipsRefreshSignal={gtReviewsRefreshSignal}
            onReadMoreTips={() => trackSectionSelected('gtReviews')}
            onAddTip={handleOpenEnrichedRecommendModal}
            onAddToTripClick={handlePlaceSaveClick}
            totalPostsCount={totalPostsCount}
            associatedPosts={associatedPosts}
            postsLoading={postsLoading}
            postsError={postsError}
            postsNeedSignIn={postsNeedSignIn}
            isLoggedIn={isLoggedIn}
            isCreatingTrip={isCreatingTrip}
            isEnriching={isEnriching}
            isStayCategory={isStayCategory}
            canBook={canBook}
            isBookLoading={isBookOpeningWhitelabel || isStay22AllezOpening}
            onBookNow={handleBookNow}
            showInlineStayBookingOptions={showInlineStayBookingOptions}
            showStay22Vendors={showStay22Vendors}
            isNuiteeBookLoading={isBookOpeningWhitelabel}
            isStay22BookLoading={isStay22AllezOpening}
            onPlaceStayBookNuitee={handlePlaceStayBookNuitee}
            onPlaceStayBookStay22Vendor={handlePlaceStayBookStay22Vendor}
            onGtExclusiveSignInClick={() => {
              requireAuth('book', () => {}, {
                title: "These rates aren't public 👀",
                description:
                  'Create a free account to view exclusive pricing and credit the creator who inspired this trip.',
              });
            }}
            hotelWidgetImpression={hotelWidgetImpression}
            canShowExperiences={canShowExperiences}
            experiencesSearchQuery={experiencesSearchQuery}
            experiencesPostId={validAttributionPostId}
            experiencesPlaceId={validAttributionPostId ? undefined : experiencesPlaceId}
            experiencesUserId={user?._id ? String(user._id) : undefined}
            experiencesGooglePlaceId={experiencesGooglePlaceId}
            onBrowseExperiences={handleBrowseExperiences}
            isExperiencesLoading={isExperiencesOpening}
            onCreateTrip={() => void handleCreateTrip()}
            onSignInClick={() => handleSignUpClick(pathname)}
            onRetryPosts={() => void fetchAssociatedPosts()}
            mapPostToCard={mapPostToTripCardProps}
          />
        )}
      </div>
    </ModalOverlay>
  );

  if (typeof window !== 'undefined') {
    return (
      <>
        {isOpen && !isBookingModalOpen && !isBookingMethodSelectionOpen && createPortal(modalContent, document.body)}

        {shareModal}

        {isLocationShareModalOpen && isValidMongoId && (
          <ShareModal
            isOpen={isLocationShareModalOpen}
            onClose={() => setIsLocationShareModalOpen(false)}
            entry="share_icon"
            resourceType="location"
            resourceId={effectiveLocationMongoId ?? extractedLocationData.locationId ?? ''}
            title={`Share ${extractedLocationData.name || 'this place'}`}
          />
        )}

        {showPopover && getPopoverProps() && (() => {
          const props = getPopoverProps();
          if (!props) return null;
          return (
            <SignUpCTAPopover
              {...props}
              redirectPath={pathname}
              onSignUpClick={() => handleSignUpClick(pathname)}
            />
          );
        })()}

        {showLoginModal && (
          <LoginModal
            onClose={() => setShowLoginModal(false)}
            redirectPath={loginModalRedirectPath || pathname || '/'}
            initialMode="signup"
          />
        )}

        {isLoggedIn && isSaveToOverlayOpen && (
          <SaveToOverlay
            isOpen={isSaveToOverlayOpen}
            onClose={() => { setIsSaveToOverlayOpen(false); setSelectedPlaceForSave(null); }}
            place={selectedPlaceForSave}
            savedItineraries={userItineraries}
            isLoading={isLoadingItineraries}
            onSaveToItinerary={handleSaveToItinerary}
            savedLists={userLists}
            isLoadingLists={isLoadingLists}
            onSaveToList={handleSaveToList}
            onNewTripOrListCreated={() => { void fetchUserItineraries(); void fetchUserLists(); }}
            title="Add to Trip or List"
            subtitle={selectedPlaceForSave?.title || extractedLocationData.name}
          />
        )}

        {enrichedRecommendLocation && (
          <RecommendThisPlaceModalLazy
            isOpen
            onClose={() => {
              setEnrichedRecommendLocation(null);
              setEnrichedRecommendInitialDoc(undefined);
            }}
            mode={enrichedRecommendInitialDoc ? 'edit' : 'create'}
            location={enrichedRecommendLocation}
            initialRecommendation={enrichedRecommendInitialDoc ?? undefined}
            hidePlaceLink
            onRecommended={(rec) => {
              const lid = rec.location_id;
              if (lid) addRecommendation(lid);
              setEnrichedRecommendInitialDoc(rec);
              refreshGtReviewsData();
              const myUserId = user?._id ? String(user._id) : '';
              if (myUserId) {
                const myAvatarUrl = (user as Record<string, unknown> | null)?.small_avatar_url as string | undefined;
                setFetchedRecAvatars((prev) => {
                  const entry: RecommenderAvatar = { userId: myUserId, avatarUrl: myAvatarUrl ?? '' };
                  if (!prev) return [entry];
                  if (prev.some((a) => a.userId === myUserId)) return prev;
                  return [...prev, entry];
                });
              }
            }}
          />
        )}

        {bookingModals}

        <BookingSsoBlockedModal
          isOpen={!!stay22AllezBlockedUrl}
          onClose={clearStay22AllezBlockedState}
          loginUrl={stay22AllezBlockedUrl}
          variant="stay22"
        />
      </>
    );
  }

  return null;
};

export default CombinedContentPlaceModal;
